import { create } from "zustand";
import {
  listOptimizerJobs,
  getOptimizerJob,
  getRunningJobCount,
} from "../services/optimizerService";
import type { OptimizerJob } from "../services/optimizerService";

interface OptimizerState {
  // Running job badge count
  runningJobCount: number;

  // Jobs list
  jobs: OptimizerJob[];
  totalJobs: number;
  currentPage: number;
  statusFilter: string | undefined;
  isLoadingJobs: boolean;

  // Single job (for results page)
  activeJob: OptimizerJob | null;
  isLoadingActiveJob: boolean;

  // Polling
  pollingIntervals: Map<string, ReturnType<typeof setInterval>>;

  // Actions
  fetchRunningCount: () => Promise<void>;
  fetchJobs: (params?: { status?: string; page?: number }) => Promise<void>;
  fetchJob: (jobId: string) => Promise<void>;

  startPolling: (jobId: string) => void;
  stopPolling: (jobId: string) => void;
  stopAllPolling: () => void;

  setStatusFilter: (status: string | undefined) => void;
  setPage: (page: number) => void;
  reset: () => void;
}

export const useOptimizerStore = create<OptimizerState>((set, get) => ({
  runningJobCount: 0,
  jobs: [],
  totalJobs: 0,
  currentPage: 1,
  statusFilter: undefined,
  isLoadingJobs: false,
  activeJob: null,
  isLoadingActiveJob: false,
  pollingIntervals: new Map(),

  fetchRunningCount: async () => {
    try {
      const count = await getRunningJobCount();
      set({ runningJobCount: count });
    } catch (err) {
      console.error("[Optimizer] Failed to fetch running count:", err);
    }
  },

  fetchJobs: async (params) => {
    try {
      set({ isLoadingJobs: true });
      const status = params?.status ?? get().statusFilter;
      const page = params?.page ?? get().currentPage;
      const resp = await listOptimizerJobs({ status, page, limit: 20 });
      set({
        jobs: resp.jobs,
        totalJobs: resp.total,
        currentPage: resp.page,
        statusFilter: status,
        isLoadingJobs: false,
      });
    } catch (err) {
      console.error("[Optimizer] Failed to fetch jobs:", err);
      set({ isLoadingJobs: false });
    }
  },

  fetchJob: async (jobId) => {
    try {
      set({ isLoadingActiveJob: true });
      const job = await getOptimizerJob(jobId);
      set({ activeJob: job, isLoadingActiveJob: false });

      // If job completed or failed, stop polling and refresh count
      if (job.status === "completed" || job.status === "failed") {
        get().stopPolling(jobId);
        get().fetchRunningCount();
      }
    } catch (err) {
      console.error("[Optimizer] Failed to fetch job:", err);
      set({ isLoadingActiveJob: false });
    }
  },

  startPolling: (jobId) => {
    const { pollingIntervals } = get();
    if (pollingIntervals.has(jobId)) return; // Already polling

    const interval = setInterval(() => {
      get().fetchJob(jobId);
    }, 5000); // Poll every 5 seconds

    pollingIntervals.set(jobId, interval);
    set({ pollingIntervals: new Map(pollingIntervals) });
  },

  stopPolling: (jobId) => {
    const { pollingIntervals } = get();
    const interval = pollingIntervals.get(jobId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.delete(jobId);
      set({ pollingIntervals: new Map(pollingIntervals) });
    }
  },

  stopAllPolling: () => {
    const { pollingIntervals } = get();
    pollingIntervals.forEach((interval) => clearInterval(interval));
    set({ pollingIntervals: new Map() });
  },

  setStatusFilter: (status) => set({ statusFilter: status, currentPage: 1 }),
  setPage: (page) => set({ currentPage: page }),
  reset: () => {
    get().stopAllPolling();
    set({
      jobs: [],
      totalJobs: 0,
      currentPage: 1,
      statusFilter: undefined,
      activeJob: null,
      isLoadingJobs: false,
      isLoadingActiveJob: false,
      runningJobCount: 0,
    });
  },
}));
